import React from 'react';
import { UseQueryResult } from 'react-query';
import { WalletData } from '../../types/crypto';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Swal from 'sweetalert2';
import { Copy, RefreshCw, AlertTriangle } from 'lucide-react';

interface WalletCardProps {
  config: any;
  query: UseQueryResult<WalletData, Error>;
}

export const WalletCard: React.FC<WalletCardProps> = ({ config, query }) => {
  const { data, isLoading, isError, error, refetch, isFetching } = query;

  const handleCopy = () => {
    Swal.fire({
      icon: 'success',
      title: 'Copied!',
      text: 'Address copied to clipboard',
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <div className="card h-100 mb-6 d-flex flex-column" style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.03)' }}>
      {/* Header */}
      <div className="card-header border-0 pt-5 pb-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-3">
            <div className="symbol symbol-45px symbol-circle bg-light-primary d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px', minWidth: '45px', flexShrink: 0 }}>
              {config.icon ? (
                <img src={config.icon} alt={config.symbol} style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              ) : (
                <span className="text-primary fw-bolder fs-4">{config.symbol[0]}</span>
              )}
            </div>
            <div>
              <h3 className="card-title align-items-start flex-column mb-0">
                <span className="card-label fw-bold text-gray-900 fs-4">{config.name}</span>
                <span className="text-muted fw-semibold fs-7 d-block mt-1">{config.subNetworks}</span>
              </h3>
            </div>
          </div>
          <div className="text-end">
            {isLoading ? (
              <div className="d-flex flex-column align-items-end gap-1">
                <div className="wallet-shimmer-bg rounded" style={{ width: '60px', height: '12px' }} />
                <div className="wallet-shimmer-bg rounded" style={{ width: '80px', height: '24px' }} />
              </div>
            ) : isError ? (
              <span className="badge badge-light-danger fs-8 fw-bold px-3 py-2">Error Fetching</span>
            ) : (
              <>
                <span className="text-gray-500 fw-semibold fs-8 d-block mb-1">Total Value</span>
                <span className="text-success fw-bolder fs-2">${data?.totalUsdValue.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="card-body pt-3 pb-5 flex-grow-1">
        {/* Address */}
        <div className="mb-7">
          <span className="text-gray-900 fw-bold fs-8 d-block mb-2">Address</span>
          <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ backgroundColor: '#F8FAFC', border: '1px solid #EFF2F5' }}>
            <span className="text-gray-700 fw-bold fs-7 d-block text-truncate" style={{ maxWidth: '85%' }}>
              {config.address}
            </span>
            <CopyToClipboard text={config.address} onCopy={handleCopy}>
              <button className="btn btn-icon btn-sm bg-white shadow-sm border-0 text-gray-600 text-hover-primary d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                <Copy size={16} />
              </button>
            </CopyToClipboard>
          </div>
        </div>

        {/* Loading / Error States */}
        {isError && (
          <div className="alert alert-danger d-flex align-items-center p-4 rounded-3 border-danger mb-0">
            <AlertTriangle size={24} className="text-danger me-3" />
            <div className="d-flex flex-column">
              <h4 className="mb-1 text-danger">Unable to fetch balance</h4>
              <span>{error?.message || 'RPC Node or API failure'}</span>
            </div>
            <button className="btn btn-sm btn-danger ms-auto fw-bold" onClick={() => refetch()}>Retry</button>
          </div>
        )}

        {isLoading && !isError && (
          <div className="mt-4">
            {/* Table Header */}
            <div className="d-flex align-items-center justify-content-between pb-3 mb-2" style={{ borderBottom: '1px solid #EFF2F5' }}>
              <div style={{ width: '40%' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>Asset</span>
              </div>
              <div style={{ width: '30%', textAlign: 'center' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>Balance</span>
              </div>
              <div style={{ width: '30%', textAlign: 'right' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>USD Value</span>
              </div>
            </div>

            {/* 3 Shimmering Rows */}
            {[1, 2, 3].map((rowIdx) => (
              <div key={rowIdx} className="d-flex align-items-center justify-content-between py-3" style={{ borderTop: rowIdx > 1 ? '1px solid #EFF2F5' : 'none' }}>
                {/* Asset Column */}
                <div className="d-flex align-items-center gap-3" style={{ width: '40%' }}>
                  <div className="wallet-shimmer-bg rounded-circle" style={{ width: '35px', height: '35px', minWidth: '35px', flexShrink: 0 }} />
                  <div className="d-flex flex-column w-100 gap-2">
                    <div className="wallet-shimmer-bg rounded" style={{ width: '60%', height: '14px' }} />
                    <div className="wallet-shimmer-bg rounded" style={{ width: '30%', height: '10px' }} />
                  </div>
                </div>
                {/* Balance Column */}
                <div className="d-flex justify-content-center" style={{ width: '30%' }}>
                  <div className="wallet-shimmer-bg rounded" style={{ width: '50%', height: '14px' }} />
                </div>
                {/* USD Value Column */}
                <div className="d-flex justify-content-end" style={{ width: '30%' }}>
                  <div className="wallet-shimmer-bg rounded" style={{ width: '45%', height: '14px' }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table layout for Assets */}
        {!isLoading && !isError && data && (
          <div className="mt-4">
            {/* Table Header */}
            <div className="d-flex align-items-center justify-content-between pb-3 mb-2" style={{ borderBottom: '1px solid #EFF2F5' }}>
              <div style={{ width: '40%' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>Asset</span>
              </div>
              <div style={{ width: '30%', textAlign: 'center' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>Balance</span>
              </div>
              <div style={{ width: '30%', textAlign: 'right' }}>
                <span className="text-gray-500 fw-bold fs-9 text-uppercase" style={{ letterSpacing: '0.5px' }}>USD Value</span>
              </div>
            </div>

            {/* Native Asset Row */}
            <div className="d-flex align-items-center justify-content-between py-3">
              <div className="d-flex align-items-center gap-3" style={{ width: '40%' }}>
                <div className="symbol symbol-35px symbol-circle bg-light-warning d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px', minWidth: '35px', flexShrink: 0 }}>
                  {config.icon ? (
                    <img src={config.icon} alt={config.symbol} style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                  ) : (
                    <span className="text-warning fw-bold fs-5">{config.symbol[0]}</span>
                  )}
                </div>
                <div className="d-flex flex-column">
                  <span className="text-gray-900 fw-bold fs-6">{config.name.replace(' Wallet', '')}</span>
                  <span className="text-muted fw-semibold fs-8">{config.symbol}</span>
                </div>
              </div>
              <div style={{ width: '30%', textAlign: 'center' }}>
                <span className="text-gray-900 fw-bold fs-6">
                  {data.nativeBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })} {data.symbol}
                </span>
              </div>
              <div style={{ width: '30%', textAlign: 'right' }}>
                <span className="text-success fw-bold fs-6">
                  ${data.nativeUsdValue.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Token Assets Rows */}
            {data.assets && data.assets
              .filter((asset: any) => {
                if (config.id === 'evm') {
                  return ['USDC', 'DAI'].includes((asset.symbol || '').toUpperCase());
                }
                return !['YRISE', 'LILPEPE', 'TRUMP', 'TMX', 'DAPPS'].includes((asset.symbol || '').toUpperCase());
              })
              .map((asset: any) => (
                <div key={asset.id} className="d-flex align-items-center justify-content-between py-3" style={{ borderTop: '1px solid #EFF2F5' }}>
                  <div className="d-flex align-items-center gap-3" style={{ width: '40%' }}>
                    {asset.icon ? (
                      <img src={asset.icon} alt={asset.symbol} className="w-35px h-35px rounded-circle" />
                    ) : (
                      <div className="w-35px h-35px rounded-circle bg-light-primary d-flex align-items-center justify-content-center">
                        <span className="text-primary fw-bold fs-6">{asset.symbol[0]}</span>
                      </div>
                    )}
                    <div className="d-flex flex-column text-truncate">
                      <span className="text-gray-900 fw-bold fs-6 text-truncate" style={{ maxWidth: '170px' }}>{asset.name.split(' · ')[0]}</span>
                      <span className="text-muted fw-semibold fs-8">{asset.symbol}</span>
                    </div>
                  </div>
                <div style={{ width: '30%', textAlign: 'center' }}>
                  <span className="text-gray-900 fw-bold fs-6">
                    {asset.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })} {asset.symbol}
                  </span>
                </div>
                <div style={{ width: '30%', textAlign: 'right' }}>
                  <span className="text-success fw-bold fs-6">
                    ${asset.usdValue.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="card-footer border-0 pt-0 pb-5 px-6 d-flex align-items-center justify-content-end mt-auto">
        <button 
          className={`btn btn-sm fw-bold border-0 d-flex align-items-center bg-gray-100 ${isFetching ? 'disabled' : ''}`}
          style={{ color: '#5E6278', padding: '0.4rem 1rem' }}
          onClick={() => refetch()}
        >
          {isFetching ? (
             <><span className="spinner-border spinner-border-sm me-2 align-middle"></span> Refreshing...</>
          ) : (
             <><RefreshCw size={14} className="me-2" /> Refresh</>
          )}
        </button>
      </div>
    </div>
  );
};
